/**
 * Route Configuration Page
 * Shows selected tasks on map and allows configuration before calling Verso API
 * Compact layout matching Simulation page - only difference is this creates actual routes
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { 
  ArrowLeft, 
  ArrowRight,
  Calendar, 
  Truck, 
  Clock, 
  DollarSign,
  AlertTriangle,
  Loader2,
  Target,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { getTasksByUids } from '../services/tasks.api';
import type { RoutingTask } from '../types';
import { setMapLanguageToArabic, ARABIC_MAP_STYLE } from '../utils/mapbox';
import { getBaghdadToday } from '../utils/date';

const BAGHDAD_WAREHOUSE = { lat: 33.284412, lng: 44.448563 };

const ZONE_COLORS: Record<string, string> = {};
const DEFAULT_COLORS = [
  '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
];

interface LocationState {
  taskUids: string[];
  routeDate?: string;
}

interface RouteConfig {
  vehicleMode: 'auto' | 'manual';
  vehicleCount: number;
  vehicleCapacity: number;
  speedFactor: number;
  startTime: string;
  endTime: string;
  breaksEnabled: boolean;
  breakDurationMinutes: number;
  optimizationGoal: 'balanced' | 'minimize_time' | 'minimize_vehicles';
  serviceTimePerParcel: number;
  baseServiceTimeMinutes: number;
  useMerchantTimeWindows: boolean;
  excludeDedicatedDriverMerchants: boolean;
  hubStart: boolean;
  costPerHour: number;
  costPerKm: number;
  fixedVehicleCost: number;
}

const DEFAULT_CONFIG: RouteConfig = {
  vehicleMode: 'auto',
  vehicleCount: 10,
  vehicleCapacity: 300,
  speedFactor: 1.0,
  startTime: '15:00',
  endTime: '23:00',
  breaksEnabled: false,
  breakDurationMinutes: 0,
  optimizationGoal: 'balanced',
  serviceTimePerParcel: 5,
  baseServiceTimeMinutes: 5,
  useMerchantTimeWindows: false,
  excludeDedicatedDriverMerchants: true,
  hubStart: true,
  costPerHour: 4,
  costPerKm: 0,
  fixedVehicleCost: 0,
};

export default function RouteConfig() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  // Map refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [tasks, setTasks] = useState<RoutingTask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<RouteConfig>(() => {
    const stored = sessionStorage.getItem('routeConfig');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_CONFIG, ...parsed };
      } catch { /* ignore */ }
    }
    return DEFAULT_CONFIG;
  });

  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    tasks: true,
    vehicle: true,
    time: false,
    optimization: false,
    cost: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Get task UIDs from navigation state
  const taskUids = useMemo(() => {
    if (state?.taskUids) return state.taskUids;
    const stored = sessionStorage.getItem('selectedTasksForRouting');
    if (stored) {
      try {
        return JSON.parse(stored) as string[];
      } catch {
        return [];
      }
    }
    return [];
  }, [state]);

  const routeDate = state?.routeDate || getBaghdadToday();

  // Fetch selected tasks
  useEffect(() => {
    const fetchTasks = async () => {
      if (taskUids.length === 0) {
        setError('No tasks selected');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await getTasksByUids(taskUids);
        const selectedTasks = response.tasks;
        setTasks(selectedTasks);
        setError(null);
      } catch (err) {
        setError('Failed to load tasks');
        console.error('Error fetching tasks:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTasks();
  }, [taskUids, routeDate]);

  // Count RTOs and Pickups
  const rtoCount = useMemo(() => tasks.filter(t => t.item_type === 'rto').length, [tasks]);
  const pickupCount = useMemo(() => tasks.filter(t => t.item_type === 'pickup').length, [tasks]);
  const totalOrders = useMemo(() => tasks.reduce((sum, t) => sum + t.number_of_orders, 0), [tasks]);

  // Initialize map
  useEffect(() => {
    if (isLoading || !mapContainer.current || map.current) return;

    const timer = setTimeout(() => {
      if (!mapContainer.current) return;
      
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: ARABIC_MAP_STYLE,
        center: [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat],
        zoom: 11,
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      
      // Set Arabic language for map labels
      setMapLanguageToArabic(map.current);
      
      map.current.on('load', () => {
        setMapLoaded(true);
        map.current?.resize();
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      map.current?.remove();
      map.current = null;
    };
  }, [isLoading]);

  // Draw tasks on map
  useEffect(() => {
    if (!map.current || !mapLoaded || tasks.length === 0) return;

    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add warehouse marker
    const warehouseEl = document.createElement('div');
    warehouseEl.innerHTML = `
      <div style="width: 36px; height: 36px; background: #7C3AED; border: 3px solid white; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M4 21V9L12 3L20 9V21H14V14H10V21H4Z"/></svg>
      </div>
    `;
    const warehouseMarker = new mapboxgl.Marker({ element: warehouseEl })
      .setLngLat([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat])
      .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML('<strong>Baghdad Sorting Center</strong>'))
      .addTo(map.current);
    markersRef.current.push(warehouseMarker);

    // Add task markers
    let colorIndex = 0;
    tasks.forEach((task) => {
      const isRTO = task.item_type === 'rto';
      const isCash = task.item_type === 'cash';
      let color: string;

      if (isRTO) {
        color = '#DC2626';
      } else if (isCash) {
        color = '#D97706';
      } else {
        color = ZONE_COLORS[task.zone_name || 'unknown'];
        if (!color) {
          color = DEFAULT_COLORS[colorIndex % DEFAULT_COLORS.length];
          ZONE_COLORS[task.zone_name || 'unknown'] = color;
          colorIndex++;
        }
      }

      const hasTimeWindow = task.has_pickup_window && task.pickup_window;
      const orderCount = task.number_of_orders ?? 0;
      const typeLabel = isRTO ? 'RTO' : 'Pickup';

      const el = document.createElement('div');
      el.innerHTML = `
        <div style="
          min-width: 24px; height: 24px; padding: 0 6px;
          background: ${color}; 
          border: 2px solid ${hasTimeWindow ? '#8B5CF6' : 'white'}; 
          border-radius: 12px; 
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: bold; color: white; cursor: pointer;
        ">${orderCount}</div>
      `;

      const timeWindowInfo = hasTimeWindow 
        ? `<div style="margin-top: 8px; padding: 6px 8px; background: #F3E8FF; border-radius: 4px; font-size: 11px; color: #7C3AED;">
            <strong>⏰ Preferred:</strong> ${task.pickup_window?.start} - ${task.pickup_window?.end}
          </div>`
        : '';

      const popup = new mapboxgl.Popup({ offset: 15 }).setHTML(`
        <div style="font-size: 12px; min-width: 150px;">
          <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
            <span style="
              display: inline-block; padding: 2px 6px; border-radius: 4px;
              font-size: 10px; font-weight: bold;
              background: ${isRTO ? '#FEE2E2' : '#DBEAFE'};
              color: ${isRTO ? '#DC2626' : '#2563EB'};
            ">${typeLabel}</span>
            <strong>${task.merchant_name || 'Unknown'}</strong>
          </div>
          <span style="color: #666;">${orderCount} orders</span>
          ${timeWindowInfo}
        </div>
      `);

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([task.lng, task.lat])
        .setPopup(popup)
        .addTo(map.current!);
      markersRef.current.push(marker);
    });

    // Fit bounds
    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat]);
    tasks.forEach(task => bounds.extend([task.lng, task.lat]));
    map.current.fitBounds(bounds, { padding: 50 });
  }, [tasks, mapLoaded]);

  // Duration display
  const durationDisplay = useMemo(() => {
    const startHour = parseInt(config.startTime.split(':')[0]);
    const endHour = parseInt(config.endTime.split(':')[0]);
    const startMins = startHour * 60 + parseInt(config.startTime.split(':')[1]);
    let endMins = endHour * 60 + parseInt(config.endTime.split(':')[1]);
    if (endHour >= 0 && endHour <= 3 && startHour > 3) endMins += 1440;
    const diff = endMins - startMins;
    return `${Math.floor(diff / 60)}h ${diff % 60 > 0 ? `${diff % 60}m` : ''}`;
  }, [config.startTime, config.endTime]);

  const handleBack = () => {
    navigate('/tasks');
  };

  const handleGenerateRoutes = () => {
    // Store configuration and navigate to preview
    sessionStorage.setItem('routeConfig', JSON.stringify(config));

    navigate('/routes/preview', {
      state: {
        taskUids,
        routeDate,
        startTime: config.startTime,
        endTime: config.endTime,
        secondsPerParcel: config.serviceTimePerParcel,
        baseServiceTimeMin: config.baseServiceTimeMinutes,
        driverMode: config.vehicleMode,
        manualDriverCount: config.vehicleMode === 'manual' ? config.vehicleCount : undefined,
        optimizationGoal: config.optimizationGoal,
        speedFactor: config.speedFactor,
        useMerchantTimeWindows: config.useMerchantTimeWindows,
        hubStart: config.hubStart,
        vehicleCapacity: config.vehicleCapacity,
        excludeDedicatedDriverMerchants: config.excludeDedicatedDriverMerchants,
        breaksEnabled: config.breaksEnabled,
        breakDurationMinutes: config.breakDurationMinutes,
        costPerHour: config.costPerHour,
        costPerKm: config.costPerKm,
        fixedVehicleCost: config.fixedVehicleCost,
      },
    });
  };

  // Section header component
  const SectionHeader = ({ title, icon: Icon, section, badge }: { title: string; icon: React.ElementType; section: keyof typeof expandedSections; badge?: string | number }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-medium text-gray-900">{title}</span>
        {badge !== undefined && (
          <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
            {badge}
          </span>
        )}
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="h-4 w-4 text-gray-400" />
      ) : (
        <ChevronDown className="h-4 w-4 text-gray-400" />
      )}
    </button>
  );

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading selected tasks...</p>
        </div>
      </div>
    );
  }

  if (error || tasks.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'No Tasks Selected'}
          </h2>
          <p className="text-gray-600 mb-4">
            Please go back and select tasks to plan routes.
          </p>
          <button 
            onClick={handleBack}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Tasks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Main Content - sidebar + map */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={handleBack} className="p-1 hover:bg-gray-100 rounded">
                <ArrowLeft className="h-4 w-4 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Route Configuration</h1>
                <p className="text-xs text-gray-500">Configure and create routes</p>
              </div>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            {/* Tasks Summary */}
            <div className="border-b border-gray-100">
              <SectionHeader title="Selected Tasks" icon={Calendar} section="tasks" badge={tasks.length} />
              {expandedSections.tasks && (
                <div className="px-3 pb-3 space-y-2">
                  <div className="text-xs text-gray-500">Date: {routeDate}</div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Total Orders:</span>
                    <span className="font-medium">{totalOrders}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                      {pickupCount} Pickups
                    </span>
                    {rtoCount > 0 && (
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                        {rtoCount} RTOs
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Vehicle Config */}
            <div className="border-b border-gray-100">
              <SectionHeader title="Vehicle" icon={Truck} section="vehicle" />
              {expandedSections.vehicle && (
                <div className="px-3 pb-3 space-y-3">
                  <div className="flex gap-1">
                    {['auto', 'manual'].map(mode => (
                      <button
                        key={mode}
                        onClick={() => setConfig(prev => ({ ...prev, vehicleMode: mode as 'auto' | 'manual' }))}
                        className={`flex-1 px-2 py-1 text-xs font-medium rounded ${
                          config.vehicleMode === mode
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {mode === 'auto' ? 'Auto' : 'Manual'}
                      </button>
                    ))}
                  </div>
                  {config.vehicleMode === 'manual' && (
                    <div>
                      <input
                        type="range" min="1" max="50" value={config.vehicleCount}
                        onChange={(e) => setConfig(prev => ({ ...prev, vehicleCount: parseInt(e.target.value) }))}
                        className="w-full h-1"
                      />
                      <div className="text-xs text-center text-blue-600 font-medium">{config.vehicleCount} vehicles</div>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-gray-500">Capacity</label>
                    <input
                      type="number" min="50" max="1000" value={config.vehicleCapacity}
                      onChange={(e) => setConfig(prev => ({ ...prev, vehicleCapacity: parseInt(e.target.value) || 300 }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Time Config */}
            <div className="border-b border-gray-100">
              <SectionHeader title="Time" icon={Clock} section="time" badge={durationDisplay} />
              {expandedSections.time && (
                <div className="px-3 pb-3 space-y-3">
                  {/* Time display */}
                  <div className="flex justify-between items-center">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">{config.startTime}</div>
                      <div className="text-xs text-gray-500">Start</div>
                    </div>
                    <div className="flex-1 flex items-center justify-center">
                      <div className="h-0.5 w-8 bg-blue-200"></div>
                      <Clock className="h-4 w-4 text-blue-400 mx-1" />
                      <div className="h-0.5 w-8 bg-blue-200"></div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">{config.endTime}</div>
                      <div className="text-xs text-gray-500">End</div>
                    </div>
                  </div>
                  
                  {/* Dual range slider */}
                  {(() => {
                    const SLIDER_MIN = 360;
                    const SLIDER_MAX = 1620;
                    const SLIDER_RANGE = SLIDER_MAX - SLIDER_MIN;
                    
                    const parseTimeToMins = (time: string): number => {
                      const hours = parseInt(time.split(':')[0]);
                      const mins = parseInt(time.split(':')[1]);
                      const totalMins = hours * 60 + mins;
                      if (hours >= 0 && hours <= 3) return totalMins + 1440;
                      return totalMins;
                    };
                    
                    const minsToTime = (mins: number): string => {
                      let adjustedMins = mins;
                      if (mins >= 1440) adjustedMins = mins - 1440;
                      const hours = Math.floor(adjustedMins / 60);
                      const minutes = adjustedMins % 60;
                      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                    };
                    
                    const startMins = parseTimeToMins(config.startTime);
                    const endMins = parseTimeToMins(config.endTime);
                    const startPercent = ((startMins - SLIDER_MIN) / SLIDER_RANGE) * 100;
                    const endPercent = ((endMins - SLIDER_MIN) / SLIDER_RANGE) * 100;
                    
                    return (
                      <div className="relative h-8 flex items-center">
                        <div className="absolute w-full h-2 bg-gray-200 rounded-full pointer-events-none">
                          <div 
                            className="absolute w-0.5 h-3 bg-gray-400 -top-0.5"
                            style={{ left: `${((1440 - SLIDER_MIN) / SLIDER_RANGE) * 100}%` }}
                            title="Midnight"
                          />
                          <div 
                            className="absolute h-2 bg-blue-500 rounded-full"
                            style={{ left: `${Math.max(0, startPercent)}%`, right: `${Math.max(0, 100 - endPercent)}%` }}
                          />
                        </div>
                        <input
                          type="range" min={SLIDER_MIN} max={SLIDER_MAX} step="30"
                          value={Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, startMins))}
                          onChange={(e) => {
                            const mins = parseInt(e.target.value);
                            if (mins < endMins - 30) setConfig(prev => ({ ...prev, startTime: minsToTime(mins) }));
                          }}
                          className="absolute w-full h-8 appearance-none bg-transparent cursor-pointer pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-green-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab"
                          style={{ zIndex: 5 }}
                        />
                        <input
                          type="range" min={SLIDER_MIN} max={SLIDER_MAX} step="30"
                          value={Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, endMins))}
                          onChange={(e) => {
                            const mins = parseInt(e.target.value);
                            if (mins > startMins + 30) setConfig(prev => ({ ...prev, endTime: minsToTime(mins) }));
                          }}
                          className="absolute w-full h-8 appearance-none bg-transparent cursor-pointer pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-red-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab"
                          style={{ zIndex: 4 }}
                        />
                      </div>
                    );
                  })()}
                  
                  <div className="text-xs text-gray-500 text-center">Drag green (start) and red (end) handles</div>
                </div>
              )}
            </div>

            {/* Optimization */}
            <div className="border-b border-gray-100">
              <SectionHeader title="Optimization" icon={Target} section="optimization" />
              {expandedSections.optimization && (
                <div className="px-3 pb-3 space-y-3">
                  <div className="flex gap-1">
                    {[
                      { value: 'balanced', label: 'Balanced' },
                      { value: 'minimize_time', label: 'Fast' },
                      { value: 'minimize_vehicles', label: 'Cheap' },
                    ].map(goal => (
                      <button
                        key={goal.value}
                        onClick={() => setConfig(prev => ({ ...prev, optimizationGoal: goal.value as RouteConfig['optimizationGoal'] }))}
                        className={`flex-1 px-2 py-1 text-xs rounded ${
                          config.optimizationGoal === goal.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {goal.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Sec/Parcel</label>
                      <input
                        type="number" min="1" max="60" value={config.serviceTimePerParcel}
                        onChange={(e) => setConfig(prev => ({ ...prev, serviceTimePerParcel: parseInt(e.target.value) || 5 }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Base Min</label>
                      <input
                        type="number" min="1" max="30" value={config.baseServiceTimeMinutes}
                        onChange={(e) => setConfig(prev => ({ ...prev, baseServiceTimeMinutes: parseInt(e.target.value) || 5 }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-gray-700">Merchant Time Windows</span>
                    <input
                      type="checkbox" checked={config.useMerchantTimeWindows}
                      onChange={(e) => setConfig(prev => ({ ...prev, useMerchantTimeWindows: e.target.checked }))}
                      className="rounded text-blue-600"
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-gray-700">Exclude Dedicated Merchants</span>
                    <input
                      type="checkbox" checked={config.excludeDedicatedDriverMerchants}
                      onChange={(e) => setConfig(prev => ({ ...prev, excludeDedicatedDriverMerchants: e.target.checked }))}
                      className="rounded text-orange-600"
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-gray-700">Start from Hub</span>
                    <input
                      type="checkbox" checked={config.hubStart}
                      onChange={(e) => setConfig(prev => ({ ...prev, hubStart: e.target.checked }))}
                      className="rounded text-blue-600"
                    />
                  </label>
                  {/* Vehicle Speed Slider */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-700">Vehicle Speed</span>
                      <span className="text-xs font-medium text-blue-600">{config.speedFactor.toFixed(1)}x</span>
                    </div>
                    <input
                      type="range"
                      min="0.5"
                      max="1.5"
                      step="0.1"
                      value={config.speedFactor}
                      onChange={(e) => setConfig(prev => ({ ...prev, speedFactor: Number(e.target.value) }))}
                      className="w-full h-1"
                    />
                    <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                      <span>0.5x Slow</span>
                      <span>1.0x Normal</span>
                      <span>1.5x Fast</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Cost */}
            <div className="border-b border-gray-100">
              <SectionHeader title="Cost" icon={DollarSign} section="cost" />
              {expandedSections.cost && (
                <div className="px-3 pb-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">$/Hour</label>
                      <input
                        type="number" min="0" step="0.5" value={config.costPerHour}
                        onChange={(e) => setConfig(prev => ({ ...prev, costPerHour: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">$/KM</label>
                      <input
                        type="number" min="0" step="0.01" value={config.costPerKm}
                        onChange={(e) => setConfig(prev => ({ ...prev, costPerKm: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Fixed</label>
                      <input
                        type="number" min="0" step="1" value={config.fixedVehicleCost}
                        onChange={(e) => setConfig(prev => ({ ...prev, fixedVehicleCost: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Create Routes Button */}
          <div className="border-t border-gray-200 p-3 space-y-3">
            {error && (
              <div className="p-2 bg-red-50 border border-red-200 rounded flex items-center gap-2 text-xs text-red-700">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{error}</span>
              </div>
            )}

            <button
              onClick={handleGenerateRoutes}
              disabled={tasks.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowRight className="h-4 w-4" />
              Preview Routes ({tasks.length} tasks)
            </button>
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 h-full relative">
          <div ref={mapContainer} className="absolute inset-0 w-full h-full" />
        </div>
      </div>
    </div>
  );
}
